import { SAMPLE_CONTRACT } from "@/mockData/contractIntelligence";
import type { ContractIntelligenceContract } from "@/types/contractIntelligence.types";
import type { ContractTemplate, ContractTemplateAnswers } from "@/types/contractIntelligence.types";

function answerString(answers: ContractTemplateAnswers, keys: string[]): string {
  for (const key of keys) {
    const val = answers[key];
    if (!val) continue;
    if (Array.isArray(val)) return val.join(", ");
    if (typeof val === "string" && val.trim()) return val.trim();
  }
  return "";
}

function formatMoney(raw: string): string {
  const digits = raw.replace(/\D/g, "");
  if (!digits) return raw;
  return `$${Number(digits).toLocaleString()}`;
}

function parseMoneyK(raw: string): number {
  const n = Number.parseInt(raw.replace(/\D/g, ""), 10);
  if (!n) return 0;
  return n >= 1_000_000 ? Math.round(n / 1000) : n;
}

function buildContractText(
  template: ContractTemplate,
  answers: ContractTemplateAnswers,
  partiesLine: string,
): string {
  const sections = template.sections
    .map((sec, i) => {
      const body = sectionBody(template.id, sec, i, answers);
      return `§ ${i + 1}. ${sec.toUpperCase()}\n${body}`;
    })
    .join("\n\n");

  return `${template.name.toUpperCase()}

Generated by Ari · ${new Date().toLocaleDateString()}

${partiesLine}

${sections}

[${Math.max(0, template.pages - template.sections.length)} additional pages of standard terms attached — governing law, dispute resolution, signatures]`;
}

function sectionBody(
  templateId: string,
  section: string,
  index: number,
  answers: ContractTemplateAnswers,
): string {
  const lower = section.toLowerCase();
  const client = answerString(answers, ["client", "artist", "parties"]);
  const brand = answerString(answers, ["brand", "label", "parties"]);
  const term = answerString(answers, ["term", "albums"]);
  const fee = answerString(answers, ["fee", "advance", "value"]);
  const royalty = answerString(answers, ["royalty", "rate", "commission"]);
  const exclusive = answerString(answers, ["exclusive"]);
  const territory = answerString(answers, ["territory"]);
  const masters = answerString(answers, ["masters"]);
  const scope = answerString(answers, ["scope"]);
  const purpose = answerString(answers, ["purpose"]);
  const notes = answerString(answers, ["notes", "carveouts"]);

  if (index === 0) {
    const partyBits = [client, brand].filter(Boolean);
    if (partyBits.length) {
      return `This Agreement is entered into by ${partyBits.join(" and ")}.`;
    }
  }

  if (lower.includes("term") && term) {
    return `The Term shall be ${term}${answerString(answers, ["options"]) ? `, subject to ${answerString(answers, ["options"])} option period(s).` : "."}`;
  }

  if (
    lower.includes("compensation") ||
    lower.includes("royalt") ||
    lower.includes("advance") ||
    lower.includes("commission")
  ) {
    const parts: string[] = [];
    if (fee) parts.push(`Guaranteed compensation: ${formatMoney(fee)}.`);
    if (royalty) parts.push(`Royalty / commission rate: ${royalty}${royalty.includes("%") ? "" : "%"}.`);
    if (exclusive) parts.push(`Exclusivity: ${exclusive}.`);
    if (territory) parts.push(`Territory: ${territory}.`);
    if (parts.length) return parts.join(" ");
  }

  if (lower.includes("master") && masters) {
    return `Master ownership: ${masters}. Label shall exploit Masters during the Term in accordance with industry custom.`;
  }

  if (lower.includes("scope") && scope) {
    return `Equal IQ shall provide the following services: ${scope}.`;
  }

  if (lower.includes("confidential") && purpose) {
    return `Confidential Information is disclosed solely for: ${purpose}.`;
  }

  if (notes) {
    return `Special terms: ${notes}`;
  }

  if (templateId === "t-nda") {
    return "Standard mutual confidentiality obligations apply.";
  }

  return "[Section drafted from your answers and attached boilerplate.]";
}

export function buildContractFromTemplate(
  template: ContractTemplate,
  answers: ContractTemplateAnswers,
): ContractIntelligenceContract {
  const client = answerString(answers, ["client", "artist", "parties"]) || "Talent";
  const brand = answerString(answers, ["brand", "label", "parties"]) || "Counterparty";
  const feeRaw = answerString(answers, ["fee", "advance", "value"]);
  const royaltyRaw = answerString(answers, ["royalty", "rate", "commission"]);
  const royaltyNum = Number.parseInt(royaltyRaw.replace(/\D/g, ""), 10) || 15;
  const advanceK = parseMoneyK(feeRaw) || SAMPLE_CONTRACT.advance;
  const term = answerString(answers, ["term", "albums"]) || "24 months";

  const partiesLine = `Parties: This ${template.name} ("Agreement") is entered into by and between ${client} ("Talent") and ${brand} ("Counterparty").`;
  const contractText = buildContractText(template, answers, partiesLine);

  const base = template.id === "t-recording" ? SAMPLE_CONTRACT : undefined;

  return {
    id: `generated-${template.id}-${Date.now()}`,
    name: `${template.name} — ${client}`,
    counterparty: brand,
    artist: client,
    type: template.name,
    typeColor: template.color,
    effectiveDate: new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }),
    pages: template.pages,
    riskScore: base?.riskScore ?? 42,
    status: "Draft",
    advance: advanceK,
    royalty: royaltyNum,
    backendRoyalty: base?.backendRoyalty ?? 3,
    term,
    masters: base?.masters ?? 0,
    keyTerms: base?.keyTerms ?? {
      financial: [
        {
          label: "Total Value",
          value: feeRaw ? formatMoney(feeRaw) : "TBD",
          sub: template.subtitle,
          flag: "low",
        },
        {
          label: "Royalty / Commission",
          value: `${royaltyNum}%`,
          sub: "Per questionnaire",
          flag: "medium",
        },
      ],
      schedule: [],
      obligations: [
        {
          type: "Generated",
          text: `Draft created from ${template.name} template`,
          risk: "low",
        },
      ],
      rights: [],
      dates: [
        {
          label: "Draft created",
          date: new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }),
          status: "upcoming",
        },
      ],
      flags: [
        {
          severity: "low",
          term: "Template draft",
          clause: "§1",
          note: "Review all sections before sending to counsel. Ari attached standard boilerplate.",
        },
      ],
    },
    contractText,
  };
}
